import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, X, MapPin, Plane, Shield, Filter, Loader2, RefreshCw, Crown, Eye, Sparkles, ChevronUp, ChevronLeft, ChevronRight, Briefcase, Languages, BookHeart, Baby, Cigarette, Wine, GraduationCap, Ruler, Church } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import PremiumModal from '../components/PremiumModal';
import VerificationModal from '../components/VerificationModal';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { Slider } from '../components/ui/slider';

import API_URL from '../config/api';
import { localizeCity } from '../utils/cityLocale';
import SEOHead from '../components/SEOHead';

const COUNTRIES = [
  { code: '', name: 'All Countries' },
  { code: 'PT', name: 'Portugal' },
  { code: 'FR', name: 'France' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'BE', name: 'Belgium' }
];

export default function Discover() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').substring(0, 2);
  
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwipping] = useState(null);
  const [showMatch, setShowMatch] = useState(null);
  const [showPremium, setShowPremium] = useState(false);
  const [premiumTrigger, setPremiumTrigger] = useState('');
  const [likesRemaining, setLikesRemaining] = useState(null);
  const [likesLimit, setLikesLimit] = useState(null);
  const [showVerification, setShowVerification] = useState(false);
  const [expandedProfile, setExpandedProfile] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const verificationShown = useRef(false);
  const swipeCount = useRef(0);
  const nudgeShown = useRef({});
  const handleSwipeRef = useRef(null);
  const [cardPhotoIndex, setCardPhotoIndex] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    min_age: 18,
    max_age: 50,
    country: '',
    city: '',
    distance: '',
    verified_only: false,
    intention: '',
    has_trip: false
  });

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.min_age) params.append('min_age', filters.min_age);
      if (filters.max_age) params.append('max_age', filters.max_age);
      if (filters.country) params.append('country', filters.country);
      if (filters.city) params.append('city', filters.city);
      if (filters.distance) params.append('distance', filters.distance);
      if (filters.verified_only) params.append('verified_only', 'true');
      if (filters.intention) params.append('intention', filters.intention);
      if (filters.has_trip) params.append('has_trip', 'true');
      
      const response = await axios.get(`${API_URL}/api/discover?${params.toString()}`, {
        withCredentials: true
      });
      
      setProfiles(response.data.profiles);
      setCurrentIndex(0);
    } catch (err) {
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleSwipe = async (action) => {
    if (swiping || currentIndex >= profiles.length) return;
    
    const profile = profiles[currentIndex];
    setSwipping(action);
    
    try {
      const response = await axios.post(`${API_URL}/api/swipe`, {
        target_user_id: profile.user_id,
        action
      }, { withCredentials: true });
      
      // Track remaining likes
      if (response.data.likes_remaining !== undefined) {
        setLikesRemaining(response.data.likes_remaining);
        setLikesLimit(response.data.likes_limit);
      }
      
      if (response.data.is_match) {
        setShowMatch(response.data.matched_user);
      }

      // ── Micro-frustration for free users (subtle, not every swipe) ──
      swipeCount.current += 1;
      const isFree = user?.current_plan === 'free';

      // ── Verification prompt after 3 swipes (only if not already verified) ──
      if (swipeCount.current === 3 && !user?.is_verified && !verificationShown.current) {
        verificationShown.current = true;
        setTimeout(() => setShowVerification(true), 800);
      }

      if (isFree && !response.data.is_match) {
        if (action === 'pass' && swipeCount.current === 5 && !nudgeShown.current['pass5']) {
          nudgeShown.current['pass5'] = true;
          setTimeout(() => {
            toast(t('premium.someone_liked_toast'), {
              description: t('premium.upgrade_to_see_gold'),
              action: { label: t('premium.see_who'), onClick: () => { setPremiumTrigger('who_liked'); setShowPremium(true); } },
              duration: 4000,
            });
          }, 800);
        }
        if (action === 'like' && response.data.likes_remaining <= 5 && response.data.likes_remaining > 0 && !nudgeShown.current['lowLikes']) {
          nudgeShown.current['lowLikes'] = true;
          setTimeout(() => {
            toast(t('premium.likes_left_toast', { count: response.data.likes_remaining }), { description: t('premium.make_them_count'), duration: 3000 });
          }, 600);
        }
      }
      
      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
        setCardPhotoIndex(0);
        setSwipping(null);
      }, 300);
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail === 'like_limit_reached') {
        setPremiumTrigger('like_limit');
        setShowPremium(true);
      } else if (err.response?.status === 403 && err.response?.data?.detail === 'email_not_verified') {
        toast.error('Please verify your email to swipe. Check your inbox!', { duration: 5000 });
      } else {
        toast.error('Failed to record swipe');
      }
      setSwipping(null);
    }
  };

  const handleUpgradeSuccess = async () => {
    await refreshUser();
    // Reset likes tracking after upgrade
    setLikesRemaining(null);
    setLikesLimit(null);
  };

  // Keep ref in sync with latest handleSwipe
  handleSwipeRef.current = handleSwipe;

  const handleKeyDown = useCallback((e) => {
    if (showMatch) return;
    if (e.key === 'ArrowLeft') handleSwipeRef.current('pass');
    if (e.key === 'ArrowRight') handleSwipeRef.current('like');
  }, [showMatch]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const currentProfile = profiles[currentIndex];

  // Calculate age from birthdate
  const getAge = (birthdate) => {
    if (!birthdate) return null;
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Get photo URL
  const getPhotoUrl = (profile) => {
    if (profile?.primary_photo?.photo_id) {
      return `${API_URL}/api/photos/${profile.primary_photo.photo_id}?auth=placeholder`;
    }
    if (profile?.google_picture) {
      return profile.google_picture;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(profile?.name || 'U')}&background=6E1A24&color=fff&size=400`;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SEOHead title="Discover" noindex path="/discover" />
      <Navigation />
      
      <div className="pt-24 pb-24 px-4 sm:px-6">
        <div className="max-w-md mx-auto">
          {/* Header with Filter */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h1 className="heading-3">{t('discover.title')}</h1>
              {/* Likes remaining badge (only for free users with limits) */}
              {likesRemaining !== null && likesLimit > 0 && (
                <div className="flex items-center gap-1.5 bg-[#6E1A24]/10 text-[#6E1A24] text-xs font-medium rounded-full px-2.5 py-1" data-testid="likes-remaining-badge">
                  <Heart className="w-3 h-3" />
                  {likesRemaining}/{likesLimit}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="rounded-full" data-testid="filter-btn">
                  <Filter className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[320px] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle className="font-serif">Filters</SheetTitle>
                </SheetHeader>
                
                <div className="mt-6 space-y-6">
                  {/* Age Range */}
                  <div>
                    <label className="label-text block mb-3">Age Range</label>
                    <div className="px-2">
                      <Slider
                        defaultValue={[filters.min_age, filters.max_age]}
                        min={18}
                        max={100}
                        step={1}
                        onValueChange={([min, max]) => setFilters(prev => ({ ...prev, min_age: min, max_age: max }))}
                        className="mb-2"
                      />
                      <div className="flex justify-between text-sm text-[#6C6C67]">
                        <span>{filters.min_age}</span>
                        <span>{filters.max_age}+</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Country */}
                  <div>
                    <label className="label-text block mb-3">Country</label>
                    <div className="grid grid-cols-2 gap-2">
                      {COUNTRIES.map(country => (
                        <button
                          key={country.code}
                          onClick={() => setFilters(prev => ({ ...prev, country: country.code }))}
                          className={`py-2 px-3 rounded-lg border text-sm transition-all ${
                            filters.country === country.code
                              ? 'border-[#6E1A24] bg-[#6E1A24]/5 text-[#6E1A24]'
                              : 'border-[#E5E3DB] hover:border-[#6E1A24]/50'
                          }`}
                        >
                          {country.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* City */}
                  <div>
                    <label className="label-text block mb-2">City</label>
                    <input
                      type="text"
                      value={filters.city}
                      onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full p-3 border border-[#E5E3DB] rounded-xl focus:outline-none focus:border-[#6E1A24]"
                      placeholder="Any city"
                    />
                  </div>

                  {/* Distance */}
                  <div data-testid="distance-filter">
                    <label className="label-text block mb-3">{t('discover.distance', 'Distance')}</label>

                    {/* Slider */}
                    <div className="px-1 mb-4">
                      <Slider
                        value={[filters.distance && parseInt(filters.distance) <= 1000 ? parseInt(filters.distance) : 1000]}
                        min={10}
                        max={1000}
                        step={10}
                        onValueChange={([val]) => {
                          setFilters(prev => ({ ...prev, distance: val >= 1000 ? '' : String(val) }));
                        }}
                        className="mb-2"
                      />
                      <div className="flex justify-between text-xs text-[#6C6C67]">
                        <span>10 km</span>
                        <span className="font-medium text-[#6E1A24]">
                          {filters.distance ? (parseInt(filters.distance) > 1000 ? '1000+ km' : `${filters.distance} km`) : t('discover.any_distance', 'Any distance')}
                        </span>
                        <span>{t('discover.any', 'Any')}</span>
                      </div>
                    </div>

                    {/* Quick-select buttons */}
                    <div className="grid grid-cols-4 gap-1.5">
                      {[
                        { value: '', label: t('discover.any', 'Any') },
                        { value: '25', label: '25 km' },
                        { value: '50', label: '50 km' },
                        { value: '100', label: '100 km' },
                        { value: '250', label: '250 km' },
                        { value: '500', label: '500 km' },
                        { value: '1000', label: '1000 km' },
                        { value: '99999', label: '1000+ km' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setFilters(prev => ({ ...prev, distance: opt.value }))}
                          className={`py-2 px-1.5 rounded-lg border text-xs font-medium transition-all ${
                            filters.distance === opt.value
                              ? 'border-[#6E1A24] bg-[#6E1A24] text-white'
                              : 'border-[#E5E3DB] hover:border-[#6E1A24]/50 text-[#6C6C67]'
                          }`}
                          data-testid={`distance-${opt.value || 'any'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#9C9C97] mt-2">{t('discover.distance_note', 'Calculated from your city to theirs')}</p>
                  </div>
                  
                  {/* Toggles */}
                  <div className="space-y-3">
                    {user?.current_plan === 'free' ? (
                      <button
                        onClick={() => { setPremiumTrigger('filter'); setShowPremium(true); }}
                        className="w-full py-3 px-4 rounded-xl bg-[#6E1A24]/5 border border-dashed border-[#6E1A24]/25 flex items-center gap-3 text-left hover:bg-[#6E1A24]/10 transition-colors"
                        data-testid="filter-premium-gate"
                      >
                        <Crown className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-[#2D2D2A]">{t('discover.filter_premium_title')}</p>
                          <p className="text-xs text-[#6C6C67]">{t('discover.filter_premium_desc')}</p>
                        </div>
                      </button>
                    ) : (
                      <>
                        <label className="flex items-center justify-between">
                          <span className="text-sm">Verified profiles only</span>
                          <input
                            type="checkbox"
                            checked={filters.verified_only}
                            onChange={(e) => setFilters(prev => ({ ...prev, verified_only: e.target.checked }))}
                            className="w-5 h-5 rounded border-[#E5E3DB] text-[#6E1A24] focus:ring-[#6E1A24]"
                          />
                        </label>
                        
                        <label className="flex items-center justify-between">
                          <span className="text-sm">Has upcoming trip</span>
                          <input
                            type="checkbox"
                            checked={filters.has_trip}
                            onChange={(e) => setFilters(prev => ({ ...prev, has_trip: e.target.checked }))}
                            className="w-5 h-5 rounded border-[#E5E3DB] text-[#6E1A24] focus:ring-[#6E1A24]"
                          />
                        </label>
                      </>
                    )}
                  </div>
                  
                  {/* Intention */}
                  <div>
                    <label className="label-text block mb-3">Looking For</label>
                    <div className="space-y-2">
                      {[
                        { value: '', label: 'Any' },
                        { value: 'serious', label: 'Serious Relationship' },
                        { value: 'casual', label: 'Taking it Slow' },
                        { value: 'friendship', label: 'Friendship First' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => setFilters(prev => ({ ...prev, intention: option.value }))}
                          className={`w-full py-2 px-3 rounded-lg border text-left text-sm transition-all ${
                            filters.intention === option.value
                              ? 'border-[#6E1A24] bg-[#6E1A24]/5 text-[#6E1A24]'
                              : 'border-[#E5E3DB] hover:border-[#6E1A24]/50'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <Button onClick={fetchProfiles} className="btn-primary w-full" data-testid="apply-filters">
                    Apply Filters
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            </div>
          </div>

          {/* Swipe Cards */}
          {loading ? (
            <div className="h-[70vh] flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-[#6E1A24] animate-spin" />
            </div>
          ) : profiles.length === 0 || currentIndex >= profiles.length ? (
            <div className="h-[70vh] flex flex-col items-center justify-center text-center px-4">
              <div className="w-24 h-24 rounded-full bg-[#6E1A24]/10 flex items-center justify-center mb-6">
                <Heart className="w-12 h-12 text-[#6E1A24]" />
              </div>
              <h2 className="text-2xl font-serif font-medium text-[#2D2D2A] mb-3">
                {profiles.length === 0 && filters.distance
                  ? t('discover.no_in_radius_title', 'No one found in your area')
                  : t('discover.no_profiles_title')}
              </h2>
              <p className="text-[#6C6C67] mb-8 max-w-sm">
                {profiles.length === 0 && filters.distance
                  ? t('discover.no_in_radius_subtitle', 'You can change your search filters to see more people.')
                  : t('discover.no_profiles_subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                {profiles.length === 0 && (
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button className="bg-[#6E1A24] text-white rounded-full px-8 py-3 font-medium hover:bg-[#8A2331] transition-all flex items-center gap-2" data-testid="modify-filters-btn">
                        <Filter className="w-4 h-4" />
                        {t('discover.modify_filters', 'Modify filters')}
                      </Button>
                    </SheetTrigger>
                  </Sheet>
                )}
                <Button onClick={fetchProfiles} className="bg-[#F5F2EB] text-[#2D2D2A] rounded-full px-8 py-3 font-medium hover:bg-[#E5E3DB] transition-all flex items-center gap-2" data-testid="refresh-profiles">
                  <RefreshCw className="w-4 h-4" />
                  {t('discover.refresh_profiles')}
                </Button>
              </div>
              
              {/* Helpful tip */}
              <div className="mt-10 p-4 bg-[#F5F2EB] rounded-2xl max-w-sm">
                {user?.current_plan === 'free' ? (
                  <button
                    onClick={() => { setPremiumTrigger('missed_match'); setShowPremium(true); }}
                    className="w-full text-center hover:opacity-80 transition-opacity"
                    data-testid="empty-state-upgrade"
                  >
                    <p className="text-sm text-[#6C6C67]">{t('discover.free_limited')}</p>
                    <p className="text-sm text-[#6E1A24] font-medium mt-0.5">{t('discover.upgrade_see_more')}</p>
                  </button>
                ) : (
                  <p className="text-sm text-[#6C6C67]">
                    {t('discover.complete_profile_tip')}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Profile Card */}
              <div className="relative">
                <div 
                  className={`profile-card ${swiping === 'pass' ? 'swipe-card swiping-left' : ''} ${swiping === 'like' ? 'swipe-card swiping-right' : ''}`}
                  data-testid="profile-card"
                >
                  {/* Multi-photo display */}
                  {(() => {
                    const allPhotos = currentProfile?.all_photos || [];
                    const photoIds = allPhotos.length > 0
                      ? allPhotos.map(p => p.photo_id)
                      : (currentProfile?.photos || []);
                    const photoCount = photoIds.length;
                    const safeIdx = Math.min(cardPhotoIndex, Math.max(0, photoCount - 1));
                    const currentPhotoUrl = photoCount > 0
                      ? `${API_URL}/api/photos/${photoIds[safeIdx]}?v=1`
                      : getPhotoUrl(currentProfile);

                    return (
                      <>
                        <img 
                          src={currentPhotoUrl}
                          alt={currentProfile?.name}
                          className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); setGalleryIndex(safeIdx); setGalleryOpen(true); }}
                        />

                        {/* Photo dots (Tinder-style bars at top) */}
                        {photoCount > 1 && (
                          <div className="absolute top-3 left-3 right-3 flex gap-1 z-10" data-testid="card-photo-dots">
                            {photoIds.map((pid, i) => (
                              <div key={pid} className={`h-[3px] rounded-full flex-1 transition-all duration-300 ${i === safeIdx ? 'bg-white' : 'bg-white/35'}`} />
                            ))}
                          </div>
                        )}

                        {/* Tap zones for photo navigation */}
                        {photoCount > 1 && (
                          <div className="absolute inset-0 z-[5] flex">
                            <button
                              onClick={(e) => { e.stopPropagation(); if (safeIdx > 0) setCardPhotoIndex(safeIdx - 1); }}
                              className="w-1/3 h-full cursor-pointer"
                              aria-label="Previous photo"
                              data-testid="card-photo-prev"
                            />
                            <button
                              onClick={(e) => { e.stopPropagation(); setGalleryIndex(safeIdx); setGalleryOpen(true); }}
                              className="flex-1 h-full cursor-pointer"
                              aria-label="Open gallery"
                              data-testid="card-photo-gallery"
                            />
                            <button
                              onClick={(e) => { e.stopPropagation(); if (safeIdx < photoCount - 1) setCardPhotoIndex(safeIdx + 1); }}
                              className="w-1/3 h-full cursor-pointer"
                              aria-label="Next photo"
                              data-testid="card-photo-next"
                            />
                          </div>
                        )}
                      </>
                    );
                  })()}

                  <div className="absolute inset-0 image-overlay" />
                  
                  {/* Profile Info — tap to expand */}
                  <div 
                    className="absolute bottom-0 left-0 right-0 p-6 text-white cursor-pointer"
                    onClick={() => setExpandedProfile(currentProfile)}
                    data-testid="expand-profile-trigger"
                  >
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {currentProfile?.is_verified && (
                        <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-sm">
                          <Shield className="w-4 h-4" />
                          {t('profile.verified', 'Verified')}
                        </div>
                      )}
                      {currentProfile?.trip_destination && (
                        <div className="trip-badge text-sm">
                          <Plane className="w-4 h-4" />
                          {currentProfile.trip_destination}
                        </div>
                      )}
                    </div>
                    
                    {/* Name & Age */}
                    <h2 className="text-3xl font-serif font-medium mb-1">
                      {currentProfile?.nickname || currentProfile?.name}
                      {currentProfile?.birthdate && (
                        <span className="font-sans font-light ml-2">{getAge(currentProfile.birthdate)}</span>
                      )}
                    </h2>
                    
                    {/* Location + Distance */}
                    <div className="flex items-center gap-1 text-white/80 mb-3">
                      <MapPin className="w-4 h-4" />
                      <span>{localizeCity(currentProfile?.city, lang)}</span>
                      {currentProfile?.country && (
                        <span className="text-white/60">
                          , {COUNTRIES.find(c => c.code === currentProfile.country)?.name || currentProfile.country}
                        </span>
                      )}
                      {currentProfile?.distance_km != null && currentProfile.distance_km > 0 && (
                        <span className="ml-1 text-white/50 text-xs">
                          &middot; {currentProfile.distance_km} km
                        </span>
                      )}
                    </div>
                    
                    {/* Bio Preview */}
                    {currentProfile?.bio && (
                      <p className="text-white/80 text-sm line-clamp-2">
                        {currentProfile.bio}
                      </p>
                    )}

                    {/* Expand hint */}
                    <div className="flex justify-center mt-2">
                      <ChevronUp className="w-5 h-5 text-white/40 animate-bounce" />
                    </div>
                  </div>
                  
                  {/* Swipe Indicators */}
                  {swiping === 'like' && (
                    <div className="absolute top-8 left-8 rotate-[-15deg] border-4 border-[#2D6A4F] text-[#2D6A4F] font-bold text-3xl px-4 py-2 rounded-lg bg-white/90 z-20">
                      LIKE
                    </div>
                  )}
                  {swiping === 'pass' && (
                    <div className="absolute top-8 right-8 rotate-[15deg] border-4 border-[#6C6C67] text-[#6C6C67] font-bold text-3xl px-4 py-2 rounded-lg bg-white/90 z-20">
                      NOPE
                    </div>
                  )}
                </div>

                {/* Swipe Buttons */}
                <div className="flex justify-center gap-6 mt-6">
                  <button
                    onClick={() => handleSwipe('pass')}
                    disabled={swiping !== null}
                    className="w-16 h-16 rounded-full bg-white border border-[#E5E3DB] flex items-center justify-center shadow-lg hover:scale-110 transition-transform disabled:opacity-50"
                    data-testid="pass-btn"
                  >
                    <X className="w-8 h-8 text-[#6C6C67]" />
                  </button>
                  
                  <button
                    onClick={() => handleSwipe('like')}
                    disabled={swiping !== null}
                    className="w-16 h-16 rounded-full bg-[#6E1A24] flex items-center justify-center shadow-lg hover:scale-110 transition-transform disabled:opacity-50"
                    data-testid="like-btn"
                  >
                    <Heart className="w-8 h-8 text-white fill-white" />
                  </button>
                </div>
              </div>

              {/* Keyboard hint */}
              <p className="text-center text-sm text-[#6C6C67] mt-4 hidden sm:block">
                Use ← → arrow keys to swipe
              </p>
            </>
          )}
        </div>
      </div>

      {/* Match Modal */}
      {showMatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/f4a26dd2-7f41-41c3-afcd-5fd316f38747/images/55bbb5f31260d553ccc8e55bdebdff1b81afa2c2dfe7060d7f89ea237a820723.png')` 
            }}
          />
          <div className="absolute inset-0 bg-[#6E1A24]/80 backdrop-blur-sm" />
          
          <div className="relative text-center text-white animate-match" data-testid="match-modal">
            <h2 className="text-4xl sm:text-5xl font-serif font-light mb-4">{t('match_modal.title')}</h2>
            <p className="text-lg text-white/80 mb-8">
              {t('match_modal.subtitle', { name: showMatch.nickname || showMatch.name })}
            </p>
            
            <div className="flex justify-center items-center gap-4 mb-8">
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white">
                <img 
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=C97A63&color=fff&size=100`}
                  alt="You"
                  className="w-full h-full object-cover"
                />
              </div>
              <Heart className="w-8 h-8 text-white fill-white" />
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white">
                <img 
                  src={getPhotoUrl(showMatch)}
                  alt={showMatch.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => navigate('/matches')}
                className="bg-white text-[#6E1A24] hover:bg-white/90 rounded-full px-8 py-3"
                data-testid="send-message-btn"
              >
                {t('match_modal.send_message')}
              </Button>
              <Button 
                onClick={() => setShowMatch(null)}
                className="bg-transparent border border-white text-white hover:bg-white/10 rounded-full px-8 py-3"
                data-testid="keep-swiping-btn"
              >
                {t('match_modal.keep_swiping')}
              </Button>
            </div>

            {/* Post-match upgrade nudge (free users only) */}
            {user?.current_plan === 'free' && (
              <button
                onClick={() => { setShowMatch(null); setPremiumTrigger('post_match'); setShowPremium(true); }}
                className="mt-6 mx-auto flex items-center gap-2 text-white/60 hover:text-white/90 text-xs transition-colors"
                data-testid="post-match-upgrade"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>{t('premium.post_match_nudge')}</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Premium Modal */}
      <PremiumModal
        isOpen={showPremium}
        onClose={() => setShowPremium(false)}
        onUpgradeSuccess={handleUpgradeSuccess}
        currentPlan={user?.current_plan || 'free'}
        triggerReason={premiumTrigger}
      />

      {/* Verification Modal */}
      <VerificationModal
        isOpen={showVerification}
        onClose={() => setShowVerification(false)}
        onVerified={() => refreshUser()}
      />

      {/* ─── Expanded Profile Modal ─── */}
      {expandedProfile && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setExpandedProfile(null)}>
          <div
            className="bg-[#FAF9F6] w-full sm:w-[420px] max-h-[90vh] rounded-t-3xl sm:rounded-3xl overflow-y-auto shadow-2xl animate-in slide-in-from-bottom"
            onClick={e => e.stopPropagation()}
            data-testid="expanded-profile-modal"
          >
            {/* Photos carousel header */}
            {(() => {
              const ep = expandedProfile;
              const photos = ep.all_photos || [];
              const photoIds = photos.length > 0 ? photos.map(p => p.photo_id) : (ep.photos || []);
              const mainUrl = photoIds.length > 0
                ? `${API_URL}/api/photos/${photoIds[0]}?v=1`
                : getPhotoUrl(ep);
              return (
                <div className="relative aspect-[3/4] w-full overflow-hidden cursor-pointer" onClick={() => { setGalleryIndex(0); setGalleryOpen(true); }}>
                  <img src={mainUrl} alt={ep.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  {/* Close button */}
                  <button onClick={(e) => { e.stopPropagation(); setExpandedProfile(null); }} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur flex items-center justify-center text-white z-10" data-testid="close-expanded-profile">
                    <X className="w-5 h-5" />
                  </button>
                  {photoIds.length > 1 && (
                    <div className="absolute top-4 left-4 bg-black/40 backdrop-blur rounded-full px-3 py-1 text-white text-xs z-10">
                      {photoIds.length} {t('profile.photos', 'photos')}
                    </div>
                  )}
                  {/* Name overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h2 className="text-3xl font-serif font-medium">
                      {ep.nickname || ep.name}
                      {ep.birthdate && <span className="font-sans font-light ml-2">{getAge(ep.birthdate)}</span>}
                    </h2>
                    <div className="flex items-center gap-1.5 text-white/80 mt-1">
                      <MapPin className="w-4 h-4" />
                      <span>{localizeCity(ep.city, lang)}{ep.country ? `, ${COUNTRIES.find(c => c.code === ep.country)?.name || ep.country}` : ''}</span>
                      {ep.distance_km > 0 && <span className="text-white/50 text-xs ml-1">&middot; {ep.distance_km} km</span>}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Profile Details */}
            <div className="p-6 space-y-5">
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                {expandedProfile.is_verified && (
                  <span className="flex items-center gap-1.5 bg-[#2D6A4F]/10 text-[#2D6A4F] text-sm font-medium px-3 py-1.5 rounded-full">
                    <Shield className="w-3.5 h-3.5" /> {t('profile.verified', 'Verified')}
                  </span>
                )}
                {expandedProfile.relationship_intention && (
                  <span className="bg-[#F5F2EB] text-[#2D2D2A] text-sm font-medium px-3 py-1.5 rounded-full">
                    {t(`profile.${expandedProfile.relationship_intention}`, expandedProfile.relationship_intention)}
                  </span>
                )}
              </div>

              {/* Bio */}
              {expandedProfile.bio && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-2">{t('profile.about', 'About')}</h3>
                  <p className="text-sm text-[#2D2D2A] leading-relaxed">{expandedProfile.bio}</p>
                </div>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-3">
                {expandedProfile.occupation && (
                  <div className="flex items-center gap-2 text-sm text-[#6C6C67]">
                    <Briefcase className="w-4 h-4 text-[#9C9C97]" />
                    <span>{expandedProfile.occupation}</span>
                  </div>
                )}
                {expandedProfile.spoken_languages?.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-[#6C6C67]">
                    <Languages className="w-4 h-4 text-[#9C9C97]" />
                    <span>{expandedProfile.spoken_languages.slice(0, 3).join(', ')}</span>
                  </div>
                )}
                {expandedProfile.education && (
                  <div className="flex items-center gap-2 text-sm text-[#6C6C67]">
                    <GraduationCap className="w-4 h-4 text-[#9C9C97]" />
                    <span>{expandedProfile.education}</span>
                  </div>
                )}
                {expandedProfile.height && (
                  <div className="flex items-center gap-2 text-sm text-[#6C6C67]">
                    <Ruler className="w-4 h-4 text-[#9C9C97]" />
                    <span>{expandedProfile.height} cm</span>
                  </div>
                )}
                {expandedProfile.religion && (
                  <div className="flex items-center gap-2 text-sm text-[#6C6C67]">
                    <Church className="w-4 h-4 text-[#9C9C97]" />
                    <span>{expandedProfile.religion}</span>
                  </div>
                )}
                {expandedProfile.smoking && (
                  <div className="flex items-center gap-2 text-sm text-[#6C6C67]">
                    <Cigarette className="w-4 h-4 text-[#9C9C97]" />
                    <span>{expandedProfile.smoking === 'yes' ? t('profile.smoker', 'Smoker') : t('profile.non_smoker', 'Non-smoker')}</span>
                  </div>
                )}
                {expandedProfile.drinking && (
                  <div className="flex items-center gap-2 text-sm text-[#6C6C67]">
                    <Wine className="w-4 h-4 text-[#9C9C97]" />
                    <span>{expandedProfile.drinking === 'yes' ? t('profile.drinks', 'Drinks') : t('profile.no_alcohol', 'No alcohol')}</span>
                  </div>
                )}
                {expandedProfile.has_children && (
                  <div className="flex items-center gap-2 text-sm text-[#6C6C67]">
                    <Baby className="w-4 h-4 text-[#9C9C97]" />
                    <span>{expandedProfile.has_children === 'yes' ? t('profile.has_kids', 'Has children') : t('profile.no_kids', 'No children')}</span>
                  </div>
                )}
              </div>

              {/* Portuguese Origin */}
              {expandedProfile.portuguese_origin && (
                <div className="bg-[#F5F2EB] rounded-2xl p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-1">{t('profile.origin', 'Portuguese Origin')}</p>
                  <p className="text-sm text-[#2D2D2A] font-medium">{expandedProfile.portuguese_origin}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-4 pt-2 pb-4">
                <button
                  onClick={() => { setExpandedProfile(null); handleSwipe('pass'); }}
                  className="flex-1 py-3.5 rounded-full border-2 border-[#E5E3DB] flex items-center justify-center gap-2 text-[#6C6C67] hover:border-[#6C6C67] transition-all"
                  data-testid="expanded-pass-btn"
                >
                  <X className="w-5 h-5" /> {t('discover.pass', 'Pass')}
                </button>
                <button
                  onClick={() => { setExpandedProfile(null); handleSwipe('like'); }}
                  className="flex-1 py-3.5 rounded-full bg-[#6E1A24] text-white flex items-center justify-center gap-2 hover:bg-[#8A2331] transition-all"
                  data-testid="expanded-like-btn"
                >
                  <Heart className="w-5 h-5" /> {t('discover.like', 'Like')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Photo Gallery Overlay ─── */}
      {galleryOpen && currentProfile && (() => {
        const gp = expandedProfile || currentProfile;
        const photos = gp.all_photos || [];
        const photoIds = photos.length > 0 ? photos.map(p => p.photo_id) : (gp.photos || []);
        if (photoIds.length === 0) return null;
        const gUrl = `${API_URL}/api/photos/${photoIds[galleryIndex]}?v=1`;
        return (
          <div className="fixed inset-0 z-[70] bg-black flex flex-col" data-testid="photo-gallery">
            {/* Header */}
            <div className="flex items-center justify-between p-4 text-white">
              <span className="text-sm font-medium">{galleryIndex + 1} / {photoIds.length}</span>
              <button onClick={() => setGalleryOpen(false)} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-testid="close-gallery">
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Photo */}
            <div
              className="flex-1 flex items-center justify-center px-4 relative"
              onTouchStart={(e) => { e.currentTarget.dataset.touchX = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                const start = parseFloat(e.currentTarget.dataset.touchX);
                const end = e.changedTouches[0].clientX;
                const diff = start - end;
                if (Math.abs(diff) > 50) {
                  if (diff > 0 && galleryIndex < photoIds.length - 1) setGalleryIndex(i => i + 1);
                  else if (diff < 0 && galleryIndex > 0) setGalleryIndex(i => i - 1);
                }
              }}
            >
              {galleryIndex > 0 && (
                <button onClick={() => setGalleryIndex(i => i - 1)} className="absolute left-2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10" data-testid="gallery-prev">
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}
              <img src={gUrl} alt="" className="max-w-full max-h-[75vh] object-contain rounded-lg" />
              {galleryIndex < photoIds.length - 1 && (
                <button onClick={() => setGalleryIndex(i => i + 1)} className="absolute right-2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10" data-testid="gallery-next">
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
            </div>
            {/* Dots */}
            <div className="flex justify-center gap-2 p-4">
              {photoIds.map((pid, i) => (
                <button key={pid} onClick={() => setGalleryIndex(i)} className={`w-2 h-2 rounded-full transition-all ${i === galleryIndex ? 'bg-white w-4' : 'bg-white/30'}`} />
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
